import React, { useState, useEffect, useCallback } from 'react';
import { authAPI } from '../../api/client';

const ApiKeyManager = () => {
    const [loading, setLoading] = useState(true);
    const [hasApiKey, setHasApiKey] = useState(false);
    const [apiKeyCreatedAt, setApiKeyCreatedAt] = useState(null);
    const [apiKeyLastUsedAt, setApiKeyLastUsedAt] = useState(null);
    const [generatedKey, setGeneratedKey] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [revoking, setRevoking] = useState(false);
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState('');

    const fetchStatus = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const response = await authAPI.getApiKeyStatus();
            setHasApiKey(response.data.hasApiKey);
            setApiKeyCreatedAt(response.data.apiKeyCreatedAt);
            setApiKeyLastUsedAt(response.data.apiKeyLastUsedAt);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load API key status');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStatus();
    }, [fetchStatus]);

    const handleGenerate = async () => {
        const action = hasApiKey ? 'regenerate' : 'generate';
        if (hasApiKey && !window.confirm('This will invalidate your current API key. Any integrations using it will stop working. Continue?')) {
            return;
        }

        try {
            setGenerating(true);
            setError('');
            const response = await authAPI.generateApiKey();
            setGeneratedKey(response.data.apiKey);
            setHasApiKey(true);
            setApiKeyCreatedAt(new Date().toISOString());
            setApiKeyLastUsedAt(null);
        } catch (err) {
            setError(err.response?.data?.error || `Failed to ${action} API key`);
        } finally {
            setGenerating(false);
        }
    };

    const handleRevoke = async () => {
        if (!window.confirm('This will permanently revoke your API key. Any integrations using it will stop working. Continue?')) {
            return;
        }

        try {
            setRevoking(true);
            setError('');
            await authAPI.revokeApiKey();
            setHasApiKey(false);
            setApiKeyCreatedAt(null);
            setApiKeyLastUsedAt(null);
            setGeneratedKey(null);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to revoke API key');
        } finally {
            setRevoking(false);
        }
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(generatedKey);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Fallback for older browsers
            const textarea = document.createElement('textarea');
            textarea.value = generatedKey;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Never';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return null; // Don't show a loader for this small section
    }

    return (
        <div className="apikey-manager" id="api-key-manager">
            <style>{`
        .apikey-manager {
          background: var(--card-bg);
          border: 1px solid var(--border-color);
          border-radius: 12px;
          padding: 24px;
          margin-bottom: 24px;
        }

        .apikey-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .apikey-title {
          font-size: 18px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .apikey-subtitle {
          font-size: 13px;
          color: var(--text-secondary);
          margin-top: 4px;
        }

        .apikey-actions {
          display: flex;
          gap: 8px;
        }

        .apikey-info {
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding: 12px 16px;
          background: var(--bg-secondary);
          border-radius: 8px;
          border: 1px solid var(--border-color);
          margin-bottom: 16px;
        }

        .apikey-info-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 13px;
        }

        .apikey-info-label {
          color: var(--text-secondary);
        }

        .apikey-info-value {
          color: var(--text-primary);
          font-weight: 500;
        }

        .apikey-generated {
          margin-top: 16px;
          padding: 16px;
          background: rgba(16, 185, 129, 0.05);
          border: 1px solid rgba(16, 185, 129, 0.2);
          border-radius: 8px;
        }

        .apikey-generated-label {
          font-size: 13px;
          font-weight: 600;
          color: #10b981;
          margin-bottom: 8px;
        }

        .apikey-generated-value {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .apikey-generated-input {
          flex: 1;
          padding: 10px 12px;
          background: var(--bg-tertiary);
          border: 1px solid var(--border-color);
          border-radius: 6px;
          font-family: monospace;
          font-size: 13px;
          color: var(--text-primary);
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .apikey-copy-btn {
          padding: 10px 16px;
          background: var(--accent-color);
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          font-size: 13px;
          white-space: nowrap;
          transition: opacity 0.2s;
        }

        .apikey-copy-btn:hover {
          opacity: 0.9;
        }

        .apikey-warning {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          margin-top: 12px;
          padding: 10px 12px;
          background: rgba(245, 158, 11, 0.08);
          border: 1px solid rgba(245, 158, 11, 0.2);
          border-radius: 6px;
          font-size: 12px;
          color: #f59e0b;
          line-height: 1.4;
        }

        .apikey-warning-icon {
          flex-shrink: 0;
          margin-top: 1px;
        }

        .apikey-error {
          margin-top: 12px;
          padding: 10px 12px;
          background: rgba(239, 68, 68, 0.08);
          border: 1px solid rgba(239, 68, 68, 0.2);
          border-radius: 6px;
          font-size: 13px;
          color: #ef4444;
        }

        .apikey-empty {
          text-align: center;
          padding: 20px;
          color: var(--text-secondary);
          font-size: 14px;
        }

        .apikey-empty p {
          margin-bottom: 16px;
        }

        @media (max-width: 768px) {
          .apikey-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }

          .apikey-actions {
            width: 100%;
          }

          .apikey-actions button {
            flex: 1;
          }

          .apikey-generated-value {
            flex-direction: column;
          }

          .apikey-copy-btn {
            width: 100%;
            text-align: center;
          }

          .apikey-info-row {
            flex-direction: column;
            align-items: flex-start;
            gap: 2px;
          }
        }
      `}</style>

            <div className="apikey-header">
                <div>
                    <h3 className="apikey-title">🔑 API Key</h3>
                    <p className="apikey-subtitle">
                        Use an API key to access your account from GPT Store, CLI tools, or other integrations
                    </p>
                </div>
                {hasApiKey && (
                    <div className="apikey-actions">
                        <button
                            className="btn btn-secondary btn-sm"
                            onClick={handleGenerate}
                            disabled={generating}
                            id="regenerate-api-key-btn"
                        >
                            {generating ? 'Generating...' : 'Regenerate'}
                        </button>
                        <button
                            className="btn btn-ghost btn-sm"
                            onClick={handleRevoke}
                            disabled={revoking}
                            style={{ color: '#ef4444' }}
                            id="revoke-api-key-btn"
                        >
                            {revoking ? 'Revoking...' : 'Revoke'}
                        </button>
                    </div>
                )}
            </div>

            {error && (
                <div className="apikey-error">{error}</div>
            )}

            {hasApiKey && !generatedKey ? (
                <div className="apikey-info">
                    <div className="apikey-info-row">
                        <span className="apikey-info-label">Created</span>
                        <span className="apikey-info-value">{formatDate(apiKeyCreatedAt)}</span>
                    </div>
                    <div className="apikey-info-row">
                        <span className="apikey-info-label">Last used</span>
                        <span className="apikey-info-value">{formatDate(apiKeyLastUsedAt)}</span>
                    </div>
                </div>
            ) : !hasApiKey && !generatedKey ? (
                <div className="apikey-empty">
                    <p>No API key generated yet.</p>
                    <button
                        className="btn btn-primary"
                        onClick={handleGenerate}
                        disabled={generating}
                        id="generate-api-key-btn"
                    >
                        {generating ? 'Generating...' : 'Generate API Key'}
                    </button>
                </div>
            ) : null}

            {generatedKey && (
                <div className="apikey-generated">
                    <div className="apikey-generated-label">
                        ✓ API Key Generated — Copy it now. You won't see it again.
                    </div>
                    <div className="apikey-generated-value">
                        <input
                            className="apikey-generated-input"
                            type="text"
                            value={generatedKey}
                            readOnly
                            id="api-key-value"
                        />
                        <button
                            className="apikey-copy-btn"
                            onClick={handleCopy}
                            id="copy-api-key-btn"
                        >
                            {copied ? '✓ Copied!' : 'Copy'}
                        </button>
                    </div>
                    <div className="apikey-warning">
                        <span className="apikey-warning-icon">⚠️</span>
                        <span>This key provides full access to your account. Do not share it publicly. Store it securely — it will not be shown again.</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ApiKeyManager;
